/*
  Sign in and come back to the same page (guest sign-in fix, 3 Oct).

  The store runs Shopify's new customer accounts. Its login route,
  routes.account_login_url, is /customer_authentication/redirect?locale=...&region_country=...,
  and that route ignores any return parameter: it always ends on the account site's
  Orders page (account.<domain>/orders). Only /customer_authentication/login honours a return,
  as return_to= with a relative storefront path (shopify.dev/docs/storefronts/themes/sign-in).
  Every guest "Sign in" link on the theme had been sending return_url= to the redirect
  route, so a teacher who signed in from a search row never came back.

  withReturn(href, back)  the login address that brings the teacher back to `back`:
                          new customer accounts -> /customer_authentication/login?...&return_to=
                          classic accounts      -> the same route with return_url= (unchanged)
  rememberLink(search)    keeps a generator lesson link's query (?grade=...&mode=custom) for
                          the generator page to put back after sign-in: in this tab's
                          sessionStorage and, for a sign-in that ends in another tab, in
                          localStorage for LINK_TTL_MS. Belt and braces: return_to already
                          carries it.
  takeLink(now)           reads it back once (and forgets it), or null.
  rememberGet(intent)     UX review 15 Oct, A4: a guest pressed "get" on a ready-made file
                          ({asset, unit: product handle, lesson: teaching-order number}).
                          Kept the same way as the link, for the same 30 minutes, so the
                          generator page can finish that one free file on its own once the
                          teacher is back. Never a price, never a way to spend: the page
                          only finishes a file the lookup says a free file covers.
  takeGet(now)            reads it back once (and forgets it), or null.

  Loaded in <head> by layout/theme.liquid, before every script that builds a sign-in link.
*/
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.TTVSignInReturn = api;
})(typeof window !== 'undefined' ? window : this, function (root) {
  'use strict';

  var LINK_KEY = 'sf-generator-lesson-link';
  var LINK_TTL_MS = 30 * 60 * 1000;
  var RETURN_RE = /([?&])return_(?:to|url)=[^&#]*/;

  // A relative storefront path only ("/pages/x?y=1"): Shopify refuses anything else, and an
  // address from outside the store must never be carried through sign-in.
  function safeBack(back) {
    var b = String(back == null ? '' : back);
    if (b.charAt(0) !== '/' || b.charAt(1) === '/' || b.charAt(1) === '\\') return '/';
    return b;
  }

  function withReturn(href, back) {
    var h = String(href || '/account/login');
    var enc = encodeURIComponent(safeBack(back));
    // A second "?" (a query appended to a route that already had one) joins with &.
    h = h.replace(/(\?[^?]*)\?/, '$1&');
    var fresh = /\/customer_authentication\//.test(h);
    if (fresh) h = h.replace('/customer_authentication/redirect', '/customer_authentication/login');
    var name = fresh ? 'return_to' : 'return_url';
    if (RETURN_RE.test(h)) return h.replace(RETURN_RE, '$1' + name + '=' + enc);
    return h + (h.indexOf('?') >= 0 ? '&' : '?') + name + '=' + enc;
  }

  function store(kind) {
    try { return root[kind] || null; } catch (e) { return null; }
  }

  // `search` is the query part of a generator link ("?grade=7&...") or a whole link.
  function rememberLink(search) {
    var s = String(search || '');
    var i = s.indexOf('?');
    if (i < 0) return false;
    s = s.slice(i);
    var ok = false;
    var ss = store('sessionStorage');
    if (ss) { try { ss.setItem(LINK_KEY, s); ok = true; } catch (e) { /* localStorage below */ } }
    var ls = store('localStorage');
    if (ls) { try { ls.setItem(LINK_KEY, JSON.stringify({ s: s, at: Date.now() })); ok = true; } catch (e) { /* return_to still carries it */ } }
    return ok;
  }

  function takeLink(now) {
    var t = now || Date.now();
    var found = null;
    var ss = store('sessionStorage');
    if (ss) {
      try { found = ss.getItem(LINK_KEY); ss.removeItem(LINK_KEY); } catch (e) { found = null; }
    }
    var ls = store('localStorage');
    if (ls) {
      var raw = null;
      try { raw = ls.getItem(LINK_KEY); ls.removeItem(LINK_KEY); } catch (e) { raw = null; }
      if (!found && raw) {
        try {
          var o = JSON.parse(raw);
          if (o && typeof o.s === 'string' && typeof o.at === 'number' && t - o.at >= 0 && t - o.at <= LINK_TTL_MS) found = o.s;
        } catch (e) { /* a broken entry is just forgotten */ }
      }
    }
    return found && found.charAt(0) === '?' ? found : null;
  }

  // A4: the one ready-made file a guest asked for, kept through sign-in.
  var GET_KEY = 'sf-generator-get-after-signin';
  function cleanGet(intent) {
    if (!intent || typeof intent !== 'object') return null;
    var asset = String(intent.asset || '');
    var unit = String(intent.unit || '').toLowerCase();
    var lesson = intent.lesson == null || intent.lesson === '' ? null : parseInt(intent.lesson, 10);
    if (!/^[a-z-]{2,20}$/.test(asset) || !/^[a-z0-9-]{1,120}$/.test(unit)) return null;
    if (lesson !== null && !(lesson >= 1 && lesson <= 60)) return null;
    return { asset: asset, unit: unit, lesson: lesson };
  }
  function rememberGet(intent) {
    var c = cleanGet(intent);
    if (!c) return false;
    var raw = JSON.stringify({ asset: c.asset, unit: c.unit, lesson: c.lesson, at: Date.now() });
    var ok = false;
    var ss = store('sessionStorage');
    if (ss) { try { ss.setItem(GET_KEY, raw); ok = true; } catch (e) { /* localStorage below */ } }
    var ls = store('localStorage');
    if (ls) { try { ls.setItem(GET_KEY, raw); ok = true; } catch (e) { /* the press is simply asked for again */ } }
    return ok;
  }
  function takeGet(now) {
    var t = now || Date.now();
    var found = null;
    ['sessionStorage', 'localStorage'].forEach(function (kind) {
      var st = store(kind);
      if (!st) return;
      var raw = null;
      try { raw = st.getItem(GET_KEY); st.removeItem(GET_KEY); } catch (e) { raw = null; }
      if (found || !raw || raw.length > 400) return;
      try {
        var o = JSON.parse(raw);
        if (o && typeof o.at === 'number' && t - o.at >= 0 && t - o.at <= LINK_TTL_MS) found = cleanGet(o);
      } catch (e) { /* a broken entry is just forgotten */ }
    });
    return found;
  }

  return {
    withReturn: withReturn, rememberLink: rememberLink, takeLink: takeLink, LINK_KEY: LINK_KEY, LINK_TTL_MS: LINK_TTL_MS,
    rememberGet: rememberGet, takeGet: takeGet, GET_KEY: GET_KEY
  };
});
